import { useCallback, useEffect, useRef, useState } from "react"
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query"
import { toast } from "sonner"
import type { BulkJobStatusResponse } from "@nexus/contracts"
import { requestExport, type ExportRequestParams } from "../api/opportunities.api"
import { formatExportErrorMessage } from "../lib/format-export-error"
import { exportStatusQueryOptions, opportunityKeys } from "@/lib/queries"
import { Sentry } from "@/lib/sentry"
import { getUserFacingApiMessage } from "@/lib/api-client"
import { trackOpportunityCsvDownloaded } from "@/lib/analytics-events"

// ─── State types ─────────────────────────────────────────────────────

export type ExportState =
  | { type: "idle" }
  | { type: "pending" }
  | { type: "polling"; correlationId: string }
  | { type: "done"; downloadUrl: string; rowCount: number }
  | { type: "error"; message: string }
  | { type: "timeout" }
  | { type: "expired" }

/** Used for statuses cached before the server started naming exports. */
const FALLBACK_EXPORT_FILE_NAME = "opportunities-export.csv"

// ─── Hook ────────────────────────────────────────────────────────────

export interface UseExportActionResult {
  /** Start an export job. */
  execute(params: ExportRequestParams): void
  /** Current state of the export. */
  state: ExportState
  /** True while an export is in progress (pending or polling). */
  isExporting: boolean
  /** Clear state and allow a new export. */
  reset(): void
}

export function useExportAction(): UseExportActionResult {
  const queryClient = useQueryClient()
  /** Wall-clock ms when this export's polling session started (for refetch max duration). */
  const pollStartedAtRef = useRef(0)
  const exportSetIdRef = useRef('')
  const [state, setState] = useState<ExportState>({ type: "idle" })
  const toastIdRef = useRef<string | number | undefined>(undefined)

  const activeCorrelationId = state.type === "polling" ? state.correlationId : null
  const pollStartedAt = state.type === "polling" ? pollStartedAtRef.current : 0

  const statusQuery = useQuery<BulkJobStatusResponse | null>({
    ...exportStatusQueryOptions(activeCorrelationId ?? "", pollStartedAt),
    enabled: state.type === "polling" && !!activeCorrelationId && pollStartedAt > 0,
  })

  const dismissLoadingToast = () => {
    if (toastIdRef.current !== undefined) toast.dismiss(toastIdRef.current)
    toastIdRef.current = undefined
  }

  // React to status changes
  useEffect(() => {
    if (state.type !== "polling") return

    if (statusQuery.isError) {
      const message = getUserFacingApiMessage(statusQuery.error)
      setState({ type: "error", message })
      dismissLoadingToast()
      toast.error("Export failed", { description: message })
      return
    }

    if (statusQuery.isFetched && statusQuery.data === null) {
      setState({ type: "expired" })
      dismissLoadingToast()
      toast.warning("Export expired — please try again")
      return
    }

    if (!statusQuery.data) return

    const { status } = statusQuery.data

    if (status === "ACCEPTED" || status === "PROCESSING") {
      toast.loading(
        status === "PROCESSING" ? "Generating CSV…" : "Preparing export…",
        { id: toastIdRef.current },
      )
      return
    }

    if (status === "COMPLETED") {
      const { downloadUrl, fileName, rowCount = 0 } = statusQuery.data

      if (!downloadUrl) {
        Sentry.captureMessage("Export completed but download URL is missing", {
          level: "error",
          tags: { exportStatus: "COMPLETED_NO_URL" },
        })
        setState({ type: "error", message: "Export completed but download URL is missing." })
        dismissLoadingToast()
        toast.error("Export failed — download URL missing")
        return
      }

      setState({ type: "done", downloadUrl, rowCount })

      trackOpportunityCsvDownloaded({
        row_count: rowCount,
        opportunity_set_id: exportSetIdRef.current,
        export_duration_ms: Date.now() - pollStartedAtRef.current,
      })

      // Trigger download via hidden anchor. Browsers ignore `download` on
      // cross-origin URLs, so the signed URL carries the filename itself and
      // this only applies to the local `data:` URL path.
      const anchor = document.createElement("a")
      anchor.href = downloadUrl
      anchor.download = fileName ?? FALLBACK_EXPORT_FILE_NAME
      document.body.appendChild(anchor)
      anchor.click()
      document.body.removeChild(anchor)

      dismissLoadingToast()
      toast.success(`Export ready — ${rowCount.toLocaleString()} rows downloaded`)
    } else if (status === "FAILED") {
      const rawMessage = statusQuery.data.message ?? statusQuery.data.error ?? "Export failed"
      // The worker already sanitizes this text via formatExportError, so it is safe to
      // show. The client formatter is a second guard so a raw GCP/JSON blob can never
      // reach the toast; without a message we fall back to the generic copy because
      // `error` is a machine code like HANDLER_ERROR.
      const userMessage = statusQuery.data.message
        ? formatExportErrorMessage(statusQuery.data.message)
        : getUserFacingApiMessage()
      Sentry.captureMessage(rawMessage, { level: "error", tags: { exportStatus: "FAILED" } })
      setState({ type: "error", message: userMessage })
      dismissLoadingToast()
      toast.error("Export failed", { description: userMessage })
    }
  }, [state.type, statusQuery.data, statusQuery.isFetched, statusQuery.isError, statusQuery.error])

  // Handle polling timeout via wall-clock timer (aligned with pollStartedAtRef)
  useEffect(() => {
    if (state.type !== "polling") return

    const TIMEOUT_MS = 10 * 60 * 1000
    const remaining = TIMEOUT_MS - (Date.now() - pollStartedAtRef.current)
    if (remaining <= 0) {
      setState({ type: "timeout" })
      dismissLoadingToast()
      toast.warning("Export timed out — please try again")
      return
    }

    const timer = setTimeout(() => {
      if (state.type !== "polling") return
      setState({ type: "timeout" })
      dismissLoadingToast()
      toast.warning("Export timed out — please try again")
    }, remaining)

    return () => clearTimeout(timer)
  }, [state.type])

  const mutation = useMutation({
    mutationFn: (params: ExportRequestParams) => requestExport(params),
    onSuccess: ({ correlationId }) => {
      pollStartedAtRef.current = Date.now()
      setState({ type: "polling", correlationId })
      toastIdRef.current = toast.loading("Preparing export…")
      queryClient.invalidateQueries({
        queryKey: opportunityKeys.exportStatus(correlationId, pollStartedAtRef.current),
      })
    },
    onError: (error: Error) => {
      const message = getUserFacingApiMessage(error)
      setState({ type: "error", message })
      toast.error("Export request failed", { description: message })
    },
  })

  const execute = useCallback(
    (params: ExportRequestParams) => {
      exportSetIdRef.current = params.setId
      setState({ type: "pending" })
      mutation.mutate(params)
    },
    [mutation],
  )

  const reset = useCallback(() => {
    if (state.type === "polling") {
      queryClient.removeQueries({
        queryKey: opportunityKeys.exportStatus(state.correlationId, pollStartedAtRef.current),
      })
    }
    pollStartedAtRef.current = 0
    dismissLoadingToast()
    setState({ type: "idle" })
  }, [queryClient, state])

  const isExporting = state.type === "pending" || state.type === "polling"

  return { execute, state, isExporting, reset }
}
